"use client";

import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { whenIdle } from "@/lib/whenIdle";

// PERF: the hero of /ai-development-company embeds the full contact form
// (~640-line client component). Rendering + hydrating it before first paint
// contributed to the page's 2.3s "element render delay" on throttled mobile.
// - Desktop (lg+): the form sits beside the hero text, so it mounts
//   immediately on hydration.
// - Mobile: the form stacks below the fold, so it mounts when the user
//   approaches it (IntersectionObserver, 800px margin) or when the browser
//   goes idle — whichever happens first.
const Form = dynamic(() => import("@/HomeFeatures/Contact/Form"), {
  ssr: false,
});

export default function DeferredHeroForm() {
  const ref = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (window.innerWidth >= 1024) {
      setMounted(true);
      return;
    }

    let done = false;
    const mount = () => {
      if (done) return;
      done = true;
      setMounted(true);
    };

    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) mount();
      },
      { rootMargin: "800px" },
    );
    if (el) io.observe(el);
    const cancelIdle = whenIdle(mount);

    return () => {
      done = true;
      io.disconnect();
      cancelIdle();
    };
  }, []);

  return (
    // min-height only while empty, so post-mount layout is identical to the
    // previous direct <Form /> render.
    <div ref={ref} className={mounted ? "w-full" : "w-full min-h-[560px]"}>
      {mounted && <Form variant="dark" type="ai_forms" />}
    </div>
  );
}
